import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import Button from '@/components/form/Button';
import { useAuth } from '@/context/AuthContext';

export default function Cookies() {
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-xl my-10">
      {!user && (
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeftIcon className="h-4 w-4" />}
          onClick={() => (window.location.href = '/')}
          className="mb-6"
        >
          Back to Home
        </Button>
      )}

      <h1 className="text-4xl font-extrabold text-blue-600 mb-10 text-center">Cookie Policy</h1>

      <div className="prose prose-lg prose-blue max-w-none">
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">1. What Are Cookies</h2>
          <div className="mt-4 text-gray-700">
            <p>
              Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us make your experience better by:
            </p>
            <ul className="mt-4 list-disc pl-6">
              <li>Keeping you signed in</li>
              <li>Remembering your preferences</li>
              <li>Understanding how you use our website</li>
              <li>Making the site work as you'd expect</li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">2. Types of Cookies We Use</h2>
          <div className="mt-4 space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Essential Cookies</h3>
              <p className="text-gray-700">Required for the website to function properly. These cannot be disabled.</p>
              <ul className="mt-2 list-disc pl-6 text-gray-600">
                <li>Authentication cookies</li>
                <li>Session management</li>
                <li>Security features</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Functional Cookies</h3>
              <p className="text-gray-700">Remember your preferences and choices to enhance your experience.</p>
              <ul className="mt-2 list-disc pl-6 text-gray-600">
                <li>Language preferences</li>
                <li>Theme settings</li>
                <li>User interface customizations</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
              <p className="text-gray-700">Help us understand how visitors interact with our website, including data collected by PostHog.</p>
              <ul className="mt-2 list-disc pl-6 text-gray-600">
                <li>Page view statistics</li>
                <li>User behavior analysis</li>
                <li>Performance monitoring</li>
                <li>PostHog analytics data</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">3. Cookie Duration</h2>
          <div className="mt-4 text-gray-700">
            <p>We use two types of cookies based on their duration:</p>
            <ul className="mt-4 list-disc pl-6">
              <li>
                <strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser
              </li>
              <li>
                <strong>Persistent Cookies:</strong> Remain on your device for a set period or until manually deleted
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">4. Managing Cookies</h2>
          <div className="mt-4 text-gray-700">
            <p>
              Most web browsers allow you to control cookies through their settings preferences. You can:
            </p>
            <ul className="mt-4 list-disc pl-6">
              <li>View cookies stored on your computer</li>
              <li>Delete individual or all cookies</li>
              <li>Block cookies from being set</li>
              <li>Allow or block cookies from specific websites</li>
            </ul>
            <p className="mt-4 text-amber-600">
              Please note that blocking some types of cookies may impact your experience on our website and limit certain functionalities.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">5. Third-Party Cookies</h2>
          <div className="mt-4 text-gray-700">
            <p>
              Some features on our website use third-party services that may set their own cookies:
            </p>
            <ul className="mt-4 list-disc pl-6">
              <li>Analytics services (e.g., PostHog)</li>
              <li>Social media sharing buttons</li>
              <li>Embedded content (e.g., YouTube videos)</li>
            </ul>
            <p className="mt-4">
              These third-party services are governed by their own privacy policies and cookie settings.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">6. Your Consent</h2>
          <div className="mt-4 text-gray-700">
            <p>
              When you first visit our website, we'll ask for your consent to use non-essential cookies. You can change your preferences at any time through our cookie settings panel.
            </p>
            <p className="mt-4">
              Essential cookies required for basic site functionality do not require consent and cannot be disabled. PostHog analytics cookies are essential for our service improvement.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">7. Updates to This Policy</h2>
          <div className="mt-4 text-gray-700">
            <p>
              We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. Any changes will be posted on this page with an updated revision date.
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 border-b pb-2">8. Contact Us</h2>
          <div className="mt-4 text-gray-700">
            <p>
              If you have any questions about our use of cookies, please contact us at:{' '}
              <a href="mailto:info@snapvote.ai" className="text-primary-600 hover:text-primary-700">
                info@snapvote.ai
              </a>
            </p>
          </div>
        </section>

        <footer className="mt-12 pt-8 border-t text-center text-gray-500">
          Last updated: March 20, 2025
        </footer>
      </div>
    </div>
  );
}